import { cn } from "@frontend/shared/lib/utils";
import { useTranslations } from "next-intl";
import type {
  ControllerRenderProps,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { z } from "zod";
import { formatRequiredLabel } from "./IntakeFormQuestion";
import type { multiSelectFieldSchema } from "./IntakeFormTypes";
import { MultiSelectDropdown } from "./MultiSelectDropdown";

const IntakeMultiSelectInput = ({
  hiddenQuestions,
  setHiddenQuestions,
  updateProgress,
  field,
  question,
  register,
  getValues,
  setValue,
  isAdmin,
  isPrint,
  initiateAutosave,
  disabled,
  useNewStyles,
  hideQuestionText,
}: {
  hiddenQuestions: Set<number>;
  setHiddenQuestions: React.Dispatch<React.SetStateAction<Set<number>>>;
  updateProgress: (updatedField: string, updatedValue: any) => void;
  field: ControllerRenderProps<Partial<any>, string>;
  question: z.infer<typeof multiSelectFieldSchema>;
  register: any;
  getValues: UseFormGetValues<Partial<any>>;
  setValue: UseFormSetValue<Partial<any>>;
  isAdmin: boolean;
  isPrint: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
  useNewStyles?: boolean;
  hideQuestionText?: boolean;
}) => {
  const choices = question.questionAttributes.choices;

  if (!choices) {
    return <></>;
  }
  const updateHiddenQuestions = (choice: any, toChecked: boolean) => {
    const tempHiddenQuestions = new Set(hiddenQuestions);
    if (toChecked) {
      if (choice.conditionalQuestions) {
        for (const q of choice.conditionalQuestions) {
          tempHiddenQuestions.delete(q);
        }
      }
    } else {
      const oldValue = getValues(field.name);
      if (choice.conditionalQuestions) {
        for (const q of choice.conditionalQuestions) {
          tempHiddenQuestions.add(q);
        }
      }
      if (choices) {
        for (const c of choices) {
          if (c.id !== choice.id && oldValue.includes(c.id)) {
            if (c.conditionalQuestions) {
              for (const q of c.conditionalQuestions) {
                tempHiddenQuestions.delete(q);
              }
            }
          }
        }
      }
    }

    const newlyHiddenQuestions = new Set(
      [...tempHiddenQuestions].filter((q) => !hiddenQuestions.has(q)),
    );
    for (const field of newlyHiddenQuestions) {
      setValue(field.toString(), "");
    }

    setHiddenQuestions(tempHiddenQuestions);
    return tempHiddenQuestions;
  };

  const choiceDropdownData = choices?.map((c) => ({
    ...c,
    label: c.value,
  }));

  const values = getValues(field.name);
  const selectedValues = values ? values : [];

  const labelText = formatRequiredLabel(
    question.questionText, // needs translation
    question.isRequired,
    hideQuestionText,
  );
  const t = useTranslations("IntakeFormStates");

  const showCheckboxes = useNewStyles
    ? choices.length < 10
    : choices.length < 4;

  return (
    <div className="flex flex-col w-full gap-1" key={question.id}>
      {showCheckboxes || isPrint ? (
        <>
          <p
            className="text-sm text-[#000000]"
            data-pdflabel="multiInput-label"
            data-questionid={question.id}
          >
            {labelText}
          </p>
          <div className="w-full min-h-12 flex flex-col gap-2">
            {choices.map((choice, i) => {
              const isSelected = selectedValues.includes(choice.id);
              return (
                <label
                  key={`${choice}${i}`}
                  className={cn(
                    "w-full items-center flex gap-4 border pl-4 py-3 cursor-pointer rounded-[4px]",
                    useNewStyles
                      ? cn(
                          "rounded-3xl p-4 bg-[#F4F4F4] justify-between border border-transparent transition-all duration-300",
                          isSelected && "bg-white border-black border",
                        )
                      : "border",
                    isAdmin && choice.critical && isSelected && "bg-yellow-300",
                  )}
                >
                  {useNewStyles && (
                    <p
                      className="text-sm"
                      data-pdflabel="multiInput-choice"
                      data-questionid={question.id}
                      data-choiceindex={i}
                    >
                      {choice.value}
                    </p>
                  )}
                  <input
                    {...field}
                    {...register(field.name)}
                    value={choice.id}
                    key={choice.value}
                    data-questionid={question.id}
                    data-pdflabel="multiInput-check"
                    data-choiceindex={i}
                    className={"accent-black w-4 h-4"}
                    type="checkbox"
                    onChange={(e) => {
                      /* You would think I'd want to call field.onChange here.
                       * For some reason, that actually ruins the form value for this question,
                       * while doing nothing works properly. I don't understand it.
                       */
                      const values = getValues(field.name);
                      const currentValues = values.length ? values : [];
                      const newValue = e.target.checked
                        ? [...currentValues, e.target.value]
                        : currentValues.filter(
                            (v: string) => v !== e.target.value,
                          );
                      const updatedHiddenQuestions = updateHiddenQuestions(
                        choice,
                        e.target.checked,
                      );
                      setValue(field.name, newValue ?? []);
                      updateProgress(field.name, newValue);
                      initiateAutosave(
                        { ...getValues(), [field.name]: newValue },
                        updatedHiddenQuestions,
                      );
                    }}
                    disabled={isAdmin || disabled}
                  />
                  {!useNewStyles && (
                    <p
                      className="text-sm font-light"
                      data-pdflabel="multiInput-choice"
                      data-questionid={question.id}
                      data-choiceindex={i}
                    >
                      {choice.value}
                    </p>
                  )}
                </label>
              );
            })}
          </div>
        </>
      ) : (
        <MultiSelectDropdown
          data={choiceDropdownData}
          labelText={labelText}
          promptText={
            field.value && field.value.length > 0
              ? (() => {
                  const races = field.value;

                  if (races.length > 0) {
                    return `${races.length} selected`;
                  }
                  return t("SelectAllAppropriateOptions");
                })()
              : t("SelectAllAppropriateOptions")
          }
          valueText="option"
          selectedValues={choiceDropdownData.filter((x) =>
            selectedValues.includes(x.id),
          )}
          onSelectValue={(datum) => {
            if (isAdmin) {
              return;
            }
            const wasSelected = selectedValues.includes(datum.id);
            const newValue = wasSelected
              ? selectedValues.filter((x: string) => x !== datum.id)
              : [...selectedValues, datum.id];
            setValue(field.name, newValue);
            const updatedHiddenQuestions = updateHiddenQuestions(
              datum,
              !wasSelected,
            );
            updateProgress(field.name, newValue.length);
            initiateAutosave(
              { ...getValues(), [field.name]: newValue },
              updatedHiddenQuestions,
            );
          }}
          intakeClassNames={
            useNewStyles
              ? cn(
                  "rounded-full px-4 py-3 bg-[#F4F4F4] justify-between border border-transparent data-[open=true]:border-black data-[open=true]:border",
                  field.value?.length > 0 && "bg-white border-black border",
                )
              : ""
          }
          pdfquestion={question}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default IntakeMultiSelectInput;
